/* AG skin — Lenis smooth scroll + GSAP ScrollTrigger scenes.
 * Every scene is (re)initialised per section so it works in the theme editor too. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasGsap = () => window.gsap && window.ScrollTrigger;
  let lenis = null;

  /* ---------- Smooth scroll ---------- */
  function initLenis() {
    // Off by default: native scrolling is already smooth on Mac trackpads/phones, and wheel
    // hijacking broke trackpad gestures. Opt in with <body data-ag-smooth="true">.
    if (document.body.dataset.agSmooth !== 'true') return;
    if (reduced || !window.Lenis || !hasGsap() || window.Shopify?.designMode) return;
    lenis = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 1, touchMultiplier: 1.2 });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    window.agLenis = lenis;
  }

  /* ---------- Preloader ---------- */
  function initLoader() {
    const loader = document.querySelector('.ag-loader');
    if (!loader) return Promise.resolve();
    const count = loader.querySelector('.ag-loader__count');
    if (reduced || !window.gsap || sessionStorageGet('ag-seen') || window.innerWidth < 750) { loader.remove(); return Promise.resolve(); }
    sessionStorageSet('ag-seen', '1');
    return new Promise((resolve) => {
      const obj = { v: 0 };
      gsap.timeline({ onComplete: () => { loader.remove(); resolve(); } })
        .to(obj, { v: 100, duration: 0.8, ease: 'power2.inOut', onUpdate: () => (count.textContent = Math.round(obj.v) + '%') })
        .to(loader, { yPercent: -100, duration: 0.7, ease: 'expo.inOut' });
      setTimeout(resolve, 500); // start scenes underneath while the curtain lifts
    });
  }
  function sessionStorageGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function sessionStorageSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }

  /* ---------- Header: hide on scroll down, menu overlay ---------- */
  function initHeader() {
    const header = document.querySelector('.ag-header');
    if (!header) return;
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 10);   // header stays visible (no hide-on-scroll)
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();   // page opened mid-scroll (refresh / back button) gets the shadow straight away

    const menu = document.querySelector('.ag-menu');
    const toggles = document.querySelectorAll('[data-ag-menu-toggle]');
    const setOpen = (open) => {
      menu?.classList.toggle('is-open', open);
      header.classList.toggle('is-menu-open', open);   // swaps to the white logo over the dark menu
      toggles.forEach((t) => t.setAttribute('aria-expanded', String(open)));
      toggles.forEach((t) => { const l = t.querySelector('[data-label]'); if (l) l.textContent = open ? l.dataset.close : l.dataset.open; });
      menu?.setAttribute('aria-hidden', String(!open));
      open ? lenis?.stop() : lenis?.start();
      document.documentElement.classList.toggle('ag-no-scroll', open);
    };
    toggles.forEach((t) => t.addEventListener('click', () => setOpen(!menu.classList.contains('is-open'))));
    document.addEventListener('keydown', (e) => e.key === 'Escape' && setOpen(false));
    menu?.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => setOpen(false)));
  }

  /* ---------- Split text into lines/words ---------- */
  function splitWords(el) {
    if (el.dataset.split) return el.querySelectorAll('.ag-word');
    el.dataset.split = '1';
    const walk = (node) => {
      [...node.childNodes].forEach((n) => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(part));
            else { const s = document.createElement('span'); s.className = 'ag-word'; s.textContent = part; frag.appendChild(s); }
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1) walk(n);
      });
    };
    walk(el);
    return el.querySelectorAll('.ag-word');
  }

  /* ---------- Scenes ---------- */
  const scenes = {
    hero(root) {
      const q = gsap.utils.selector(root);
      const items = q('.ag-hero__item');
      const n = items.length;
      if (!n) return;
      const mobile = () => window.innerWidth < 750;
      // Decode every jar up front (during the intro) — otherwise the browser decodes each big
      // image the first time it scrolls into view, which is what made the first scroll stutter.
      items.forEach((el) => { const img = el.querySelector('img'); if (img) { img.loading = 'eager'; img.decode?.().catch(() => {}); } });

      // Wave conveyor. d = distance of a jar from the centre of the screen, in "jar slots".
      // Four jars visible: d = -1.5, -0.5 (big), +0.5 (big), +1.5. Jars flow right → left
      // along a falling slope with a gentle swell, so each jar's height/tilt changes as it travels.
      // Client mockup: ONE centre focus jar (d = 0), a medium jar either side (d = ±1) and a
      // cropped, softly blurred jar at each edge (d = ±2). Jars flow right → left on a gentle
      // rising wave; values between slots are smoothly interpolated.
      const START = Math.min(2, Math.floor((n - 1) / 2));   // centre jar at load
      const END = Math.max(START + 1, n - 3);
      const D = [-3, -2, -1, 0, 1, 2, 3];
      const DESK = { x: [-0.2, 0.02, 0.21, 0.49, 0.8, 0.995, 1.2], y: [0.24, 0.3, 0.42, 0.52, 0.57, 0.68, 0.74], s: [0.45, 0.52, 0.63, 1, 0.65, 0.52, 0.45] };
      // Mobile: measured from the reference at 390×844 — three jars: cropped edge · centre · cropped edge.
      const MOB  = { x: [-1.05, -0.52, 0.0, 0.5, 1.0, 1.52, 2.05], y: [0.31, 0.35, 0.39, 0.45, 0.48, 0.51, 0.55], s: [0.38, 0.44, 0.52, 1, 0.5, 0.44, 0.38] };
      const smooth = (t) => t * t * (3 - 2 * t);
      const interp = (arr, d) => {
        if (d <= D[0]) return arr[0];
        if (d >= D[D.length - 1]) return arr[arr.length - 1];
        const k = Math.floor(d - D[0]); const t = smooth(d - D[0] - k);
        return arr[k] + (arr[k + 1] - arr[k]) * t;
      };
      const tilt = [-8, 6, -4, 7, -6];                     // jars stay nearly upright, each with its own lean
      const place = (d, i, W, H) => {
        const K = mobile() ? MOB : DESK;
        const ad = Math.abs(d);
        return {
          x: interp(K.x, d) * W, y: interp(K.y, d) * H, s: interp(K.s, d),
          r: tilt[i % tilt.length] + Math.sin(d * 1.2) * 4,
          blur: mobile() ? 0 : Math.min(2.5, Math.max(0, ad - 1.2) * 3),   // no live blur on phones (GPU cost)
          op: mobile() ? Math.max(0.75, 1 - Math.max(0, ad - 0.6) * 0.5) : 1,
          z: 100 - Math.round(ad * 10),
        };
      };
      const state = { target: START, current: START };
      // Centre each jar on its point inside GSAP's own transform (CSS `translate` is not reliably
      // combined with GSAP transforms across Chrome versions — it was dropped, offsetting every jar).
      gsap.set(items, { xPercent: -50, yPercent: -50, translate: 'none', transformOrigin: '50% 50%' });
      const set = items.map((el) => ({
        x: gsap.quickSetter(el, 'x', 'px'), y: gsap.quickSetter(el, 'y', 'px'),
        sx: gsap.quickSetter(el, 'scaleX'), sy: gsap.quickSetter(el, 'scaleY'),
        r: gsap.quickSetter(el, 'rotate', 'deg'), el, blur: -1, z: -1,
      }));
      // Measure once (and on resize) — never read layout inside the per-frame loop.
      let W = 0, H = 0;
      const measure = () => {
        W = root.clientWidth; H = root.clientHeight;
        root.style.setProperty('--ag-jar-w', Math.min(W * (mobile() ? 0.6 : 0.27), H * (mobile() ? 0.44 : 0.47)) + 'px');
      };
      const layout = (index) => {
        set.forEach((st, i) => {
          const p = place(i - index, i, W, H);
          st.x(p.x); st.y(p.y); st.sx(p.s); st.sy(p.s); st.r(p.r);
          // Blur in fixed steps so a jar is only re-rasterised when it crosses a step.
          const b = Math.round(p.blur / 1.2) * 1.2;
          if (b !== st.blur) { st.blur = b; st.el.style.filter = b ? `blur(${b}px)` : 'none'; }
          if (p.z !== st.z) { st.z = p.z; st.el.style.zIndex = p.z; }
          const o = Math.round(p.op * 20) / 20;
          if (o !== st.op) { st.op = o; st.el.style.opacity = o; }
        });
      };
      measure();
      // Re-measure whenever the hero's box changes (toolbar settling, preview bar, resize, fonts/images).
      if (window.ResizeObserver) new ResizeObserver(() => { measure(); layout(state.current); }).observe(root);
      window.addEventListener('load', () => { measure(); layout(state.current); }, { once: true });

      // Intro: gentle rise IN PLACE. We do NOT fade the row from opacity 0 — that would re-hide the
      // focus jar (the LCP element, already painted via CSS) and delay LCP. Only the non-focus jars
      // (hidden until now) and the badge animate in.
      gsap.timeline({ defaults: { ease: 'expo.out', duration: 1.4 } })
        .from(q('.ag-hero__item:not(.ag-hero__item--focus):not(.ag-hero__item--pre):not(.ag-hero__item--post)'), { opacity: 0, duration: 1 }, 0)
        .from(q('.ag-hero__row-inner'), { y: 24 }, 0)
        .from(q('.ag-hero__badge'), { y: () => window.innerHeight * 0.45, opacity: 0 }, 0.1)
        .from(q('.ag-hero__scroll'), { opacity: 0, duration: 1 }, 0.8);

      // Scroll sets a target; a per-frame ease glides the jars toward it (smooth on trackpads).
      layout(START);
      root.classList.add('is-ready');
      // Wheel colour follows the jar in focus (product Primary color metafield → data-c1/c2).
      let focus = -1;
      const recolor = (index) => {
        const k = Math.max(0, Math.min(n - 1, Math.round(index)));
        if (k === focus) return;
        focus = k;
        const it = items[k];
        if (it.dataset.c1) { root.style.setProperty('--hero-c1', it.dataset.c1); root.style.setProperty('--hero-c2', it.dataset.c2); }
      };
      recolor(START);
      const art = q('.ag-hero__badge-art');
      const swipe = mobile();
      // Back wheel (curved badge) rotates with the conveyor on every device, driven by the eased
      // state.current — so it stays seamlessly in sync whether moved by autoplay, scroll or swipe.
      const spin = art.length ? gsap.quickSetter(art[0], 'rotate', 'deg') : null;
      gsap.ticker.add((time, delta) => {
        const diff = state.target - state.current;
        if (Math.abs(diff) < 0.0005) return;
        state.current += diff * (1 - Math.pow(1 - (swipe ? 0.14 : 0.1), delta / 16.67));
        layout(state.current);
        recolor(state.current);
        if (spin) spin(-28 * (state.current - START));
      });

      // Autoplay: slide one product at a time into focus (ping-pong across the range), reusing the
      // same conveyor ease. Pauses on drag, while the page is scrolling, off-screen, hidden tab or
      // reduced motion — so it never fights the user and costs nothing when not visible.
      const autoSec = Number(root.dataset.autoplay) || 0;
      if (autoSec > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        let dir = 1, hold = 0, off = false;
        const step = () => {
          if (off || document.hidden || performance.now() < hold) return;
          let t = Math.round(state.target) + dir;
          if (t >= END) { t = END; dir = -1; }
          if (t <= START) { t = START; dir = 1; }
          state.target = t;
        };
        // Hold the first advance for ~5s so the LCP (the focus jar) settles before any bottle moves
        // into/out of the centre — otherwise each cycled bottle re-registers as a new largest paint.
        setTimeout(() => setInterval(step, autoSec * 1000), 5000);
        root.addEventListener('pointerdown', () => { hold = performance.now() + 5000; });
        window.addEventListener('scroll', () => { hold = performance.now() + 1500; }, { passive: true });
        if (window.IntersectionObserver) new IntersectionObserver(([e]) => { off = !e.isIntersecting; }).observe(root);
      }

      // Phones: swipe left/right turns the wheel; vertical swipes scroll the page as normal
      // (no pinning, so the visitor is never stuck in the hero).
      if (swipe) {
        const lo = Math.min(1, n - 1), hi = Math.max(lo, n - 2);
        const clamp = (v) => Math.max(lo - 0.35, Math.min(hi + 0.35, v));
        let sx = 0, sy = 0, base = 0, active = false, decided = false, moved = false, lx = 0, lt = 0, vel = 0;
        root.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          active = true; decided = false; moved = false;
          sx = lx = e.clientX; sy = e.clientY; base = state.target; lt = performance.now(); vel = 0;
        });
        root.addEventListener('pointermove', (e) => {
          if (!active) return;
          const dx = e.clientX - sx, dy = e.clientY - sy;
          if (!decided) {
            if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { active = false; return; }
            if (Math.abs(dx) < 8) return;
            decided = moved = true; root.classList.add('is-swiping');
            try { root.setPointerCapture(e.pointerId); } catch (err) {}
          }
          const now = performance.now();
          vel = (e.clientX - lx) / Math.max(now - lt, 1); lx = e.clientX; lt = now;
          state.target = clamp(base - dx / (W * 0.5));
        });
        const end = () => {
          if (!active) return;
          active = false;
          root.classList.remove('is-swiping');
          if (!moved) return;
          const fling = Math.abs(vel) > 0.35 ? -Math.sign(vel) * 0.6 : 0;
          state.target = Math.max(lo, Math.min(hi, Math.round(state.target + fling)));
        };
        root.addEventListener('pointerup', end);
        root.addEventListener('pointercancel', end);
        // A swipe must not also open the jar's product page.
        root.addEventListener('click', (e) => { if (moved) { e.preventDefault(); moved = false; } }, true);
        window.addEventListener('resize', () => { measure(); layout(state.current); });
        return;
      }
      gsap.timeline({
        scrollTrigger: {
          trigger: root, start: 'top top', end: () => '+=' + window.innerHeight * 0.55 * (END - START),
          pin: true, scrub: true, invalidateOnRefresh: true, anticipatePin: 1,
          onUpdate: (self) => { state.target = START + (END - START) * self.progress; },
          onRefresh: () => { measure(); layout(state.current); },
        },
      })
        .to({}, { duration: 1 });   // badge rotation now handled by the ticker (in sync with the conveyor)
      window.addEventListener('resize', () => { measure(); layout(state.current); });
    },

    slider(root) {
      const track = root.querySelector('.ag-slider__track');
      const slides = [...root.querySelectorAll('.ag-slide')];
      const bar = root.querySelector('.ag-slider__progress span');
      if (!track || !slides.length) return;
      const setActive = (i) => {
        const s = slides[i];
        root.style.setProperty('--ag-bg', s.dataset.bg);
        root.style.setProperty('--ag-fg', s.dataset.fg);
      };
      setActive(0);
      const distance = () => Math.max(0, track.scrollWidth - window.innerWidth);
      const tween = gsap.to(track, {
        x: () => -distance(), ease: 'none',
        scrollTrigger: {
          trigger: root, start: 'top top', end: () => '+=' + distance(), pin: true, scrub: 1, invalidateOnRefresh: true,
          onUpdate: (self) => {
            if (bar) bar.style.transform = `scaleX(${self.progress})`;
            setActive(Math.min(slides.length - 1, Math.round(self.progress * (slides.length - 1))));
          },
        },
      });
      slides.forEach((s) => {
        const img = s.querySelector('.ag-turn') || s.querySelector('.ag-slide__img');
        const text = s.querySelectorAll('.ag-slide__body > *');
        if (img) gsap.fromTo(img, { rotate: 18, yPercent: 10 }, { rotate: -6, yPercent: -6, ease: 'none',
          scrollTrigger: { trigger: s, containerAnimation: tween, start: 'left right', end: 'right left', scrub: true } });
        // Cards visible at load (2/3/4 per view) show their details right away; the rest reveal
        // as they slide in — and early (at the right edge), so text never lags behind the jar.
        if (s.getBoundingClientRect().left < window.innerWidth) return;
        gsap.from(text, { y: 30, opacity: 0, stagger: 0.05, duration: 0.6, ease: 'power3.out',
          scrollTrigger: { trigger: s, containerAnimation: tween, start: 'left 105%', once: true } });
      });
    },

    story(root) {
      const words = splitWords(root.querySelector('.ag-story__text'));
      gsap.to(words, { opacity: 1, stagger: 0.08, ease: 'none',
        scrollTrigger: { trigger: root, start: 'top 70%', end: 'bottom 70%', scrub: true } });
      root.querySelectorAll('.ag-story__float').forEach((f, i) => {
        gsap.fromTo(f, { yPercent: i ? 60 : -40, rotate: i ? 20 : -20 }, { yPercent: i ? -60 : 40, rotate: 0, ease: 'none',
          scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true } });
      });
    },

    marquee(root) {
      const inner = root.querySelector('.ag-marquee__inner');
      const loop = gsap.to(inner, { xPercent: -50, duration: Number(root.dataset.speed || 30), ease: 'none', repeat: -1 });
      ScrollTrigger.create({ trigger: root, start: 'top bottom', end: 'bottom top', onToggle: (self) => (self.isActive ? loop.play() : loop.pause()) });
      ScrollTrigger.create({ trigger: root, onUpdate: (self) => gsap.to(loop, { timeScale: self.direction * (1 + Math.min(Math.abs(self.getVelocity()) / 600, 4)), duration: 0.3, overwrite: true, onComplete: () => gsap.to(loop, { timeScale: self.direction, duration: 0.6 }) }) });
    },

    pdp(root) {
      const img = root.querySelector('.ag-pdp__img');
      // Bottle rise is a pure CSS animation (starts at first paint → fast LCP, no JS wait).
      // clearProps: a leftover transform on an ancestor would break the fixed (sticky) mobile buy bar.
      // Phones: the buy form holds the fixed bottom bar — never transform it (a transformed ancestor
      // makes "fixed" relative to it, so the bar showed mid-page and then jumped). The bar has its own
      // CSS slide-up instead; only the other info items animate.
      const phone = window.matchMedia('(max-width: 749px)').matches;
      const intro = [...root.querySelectorAll('.ag-pdp__info > *')].filter((el) => !(phone && el.classList.contains('ag-pdp__form')));
      // If JS boots after first paint (slow devices), skip the "hide then reveal" to avoid a flash/stutter;
      // just show the content. Otherwise run a light, GPU-composited rise so it never drops frames.
      const lateBoot = performance.now() > 1200;
      if (!lateBoot) {
        gsap.from(intro, { y: 22, autoAlpha: 0, stagger: 0.05, duration: 0.6, ease: 'power2.out', delay: 0.15,
          force3D: true, overwrite: 'auto', clearProps: 'transform,opacity,visibility,willChange',
          onStart: () => intro.forEach((el) => (el.style.willChange = 'transform, opacity')) });
      }
      const wrap = root.querySelector('.ag-pdp__img-wrap');
      if (wrap && window.matchMedia('(min-width: 990px)').matches)
        gsap.fromTo(wrap, { rotate: 0, yPercent: 0 }, { rotate: 10, yPercent: -8, ease: 'none', scrollTrigger: { trigger: root, start: 'top top', end: 'bottom bottom', scrub: true } });
    },

    collection(root) {
      // Title: letters rise in.
      const title = root.querySelector('.ag-coll__title');
      if (title && !title.dataset.split) {
        title.dataset.split = '1';
        title.innerHTML = [...title.textContent].map((c) => c === ' ' ? ' ' : `<span class="ag-char">${c}</span>`).join('');
      }
      gsap.from(root.querySelectorAll('.ag-coll__title .ag-char'), { yPercent: 110, opacity: 0, stagger: 0.03, duration: 1, ease: 'expo.out' });

      // Hero image: rounded inset card → full-bleed as you scroll (clip-path only = cheap).
      const media = root.querySelector('.ag-coll__media');
      const mobile = window.innerWidth < 750;
      if (media) {
        const st = { inset: mobile ? 16 : 30, radius: mobile ? 18 : 24 };
        const apply = () => { media.style.setProperty('--ag-inset', st.inset + 'px'); media.style.setProperty('--ag-radius', st.radius + 'px'); };
        apply();
        gsap.to(st, { inset: 0, radius: 0, ease: 'none', onUpdate: apply,
          scrollTrigger: { trigger: root.querySelector('.ag-coll__frame'), start: 'top 75%', end: 'top top', scrub: true } });
        const img = media.querySelector('img');
        if (img && img.parentElement === media)
          gsap.fromTo(img, { yPercent: -8 }, { yPercent: 0, ease: 'none', scrollTrigger: { trigger: media, start: 'top bottom', end: 'bottom top', scrub: true } });
      }

      // Intro: words light up as you read.
      const intro = root.querySelector('.ag-coll__intro-text');
      if (intro) gsap.to(splitWords(intro), { opacity: 1, stagger: 0.06, ease: 'none',
        scrollTrigger: { trigger: intro, start: 'top 80%', end: 'bottom 55%', scrub: true } });

      // Cards rise in per group.
      root.querySelectorAll('.ag-coll__group').forEach((g) => {
        gsap.from(g.querySelectorAll('.ag-coll__group-title, .ag-card'), { y: 50, opacity: 0, stagger: 0.07, duration: 0.9, ease: 'power3.out', clearProps: 'transform,opacity',
          scrollTrigger: { trigger: g, start: 'top 80%' } });
      });

      // Bottom category nav: show once products are reached, highlight the current group.
      const nav = root.querySelector('.ag-coll__nav');
      if (nav) {
        const groups = [...root.querySelectorAll('.ag-coll__group')];
        const pills = [...nav.querySelectorAll('.ag-coll__pill')];
        ScrollTrigger.create({ trigger: groups[0], endTrigger: groups[groups.length - 1], start: 'top 85%', end: 'bottom 40%',
          onToggle: (self) => nav.classList.toggle('is-visible', self.isActive) });
        groups.forEach((g, i) => ScrollTrigger.create({ trigger: g, start: 'top 55%', end: 'bottom 55%',
          onToggle: (self) => { if (self.isActive) { pills.forEach((p) => p.classList.remove('is-active')); pills[i]?.classList.add('is-active'); pills[i]?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' }); } } }));
        pills.forEach((p) => p.addEventListener('click', (e) => {
          const t = document.querySelector(p.getAttribute('href'));
          if (!t) return; e.preventDefault();
          window.scrollTo({ top: t.getBoundingClientRect().top + scrollY - 70, behavior: 'smooth' });
        }));
      }
    },

    need(root) {
      const tabs = [...root.querySelectorAll('.ag-need__tab')];
      const panels = [...root.querySelectorAll('.ag-need__panel')];
      const show = (i) => {
        tabs.forEach((t, j) => { t.classList.toggle('is-active', i === j); t.setAttribute('aria-selected', String(i === j)); });
        panels.forEach((p, j) => { p.hidden = i !== j; p.classList.toggle('is-active', i === j); });
        const p = panels[i]; if (!p) return;
        root.style.setProperty('--ag-need-c', getComputedStyle(p).getPropertyValue('--ag-c'));
        gsap.fromTo(p.querySelector('.ag-need__img'), { yPercent: 60, rotate: -8, opacity: 0 }, { yPercent: 0, rotate: 0, opacity: 1, duration: 0.9, ease: 'expo.out' });
        gsap.fromTo(p.querySelector('.ag-need__blob'), { scale: 0.6 }, { scale: 1, duration: 0.8, ease: 'expo.out' });
        gsap.from(p.querySelectorAll('.ag-need__copy > *, .ag-need__benefits li'), { y: 18, opacity: 0, stagger: 0.04, duration: 0.5, ease: 'power3.out', clearProps: 'transform,opacity' });
      };
      tabs.forEach((t, i) => t.addEventListener('click', () => show(i)));
      if (panels[0]) root.style.setProperty('--ag-need-c', getComputedStyle(panels[0]).getPropertyValue('--ag-c'));
      ScrollTrigger.create({ trigger: root, start: 'top 70%', once: true, onEnter: () => show(0) });
      gsap.from(root.querySelectorAll('.ag-need__head > *, .ag-need__tab'), { y: 24, opacity: 0, stagger: 0.05, duration: 0.7, ease: 'power3.out', clearProps: 'transform,opacity', scrollTrigger: { trigger: root, start: 'top 80%' } });
    },

    why(root) {
      root.querySelectorAll('[data-ag-count]').forEach((el) => {
        const end = Number(el.dataset.agCount) || 0; const o = { v: 0 };
        el.textContent = '0';
        gsap.to(o, { v: end, duration: 1.6, ease: 'power2.out', onUpdate: () => (el.textContent = Math.round(o.v).toLocaleString()),
          scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
      });
      gsap.from(root.querySelectorAll('.ag-why__head > *, .ag-why__stat'), { y: 30, opacity: 0, stagger: 0.07, duration: 0.8, ease: 'power3.out', clearProps: 'transform,opacity', scrollTrigger: { trigger: root, start: 'top 80%' } });
      // The review carousel (arrows + auto-advance) is wired up by initReviewCarousels(),
      // which runs for every viewport and does not depend on GSAP or reduced-motion.
    },

    footer(root) {
      const big = root.querySelector('.ag-footer__big');
      if (big) gsap.from(big, { yPercent: 60, ease: 'none', scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom bottom', scrub: true } });
    },
  };

  function initSection(root) {
    const name = root.dataset.agScene;
    if (!name) return;
    // Phones always swipe (2×2 pages, native scroll-snap) — never the scroll-pinned slider.
    const phone = window.matchMedia('(max-width: 749px)').matches;
    if (name === 'slider' && (reduced || !hasGsap() || phone)) {
      root.classList.add('is-static');
      const slides = root.querySelectorAll('.ag-slide');
      if (slides[0]) { root.style.setProperty('--ag-bg', slides[0].dataset.bg); root.style.setProperty('--ag-fg', slides[0].dataset.fg); }
      if (phone && !root.dataset.agPaged) {
        // 2×2 pages: the grid fills column by column, so re-order each group of 4 (a b c d → a c b d)
        // to make every page read left-to-right, top row first.
        root.dataset.agPaged = '1';
        const track0 = root.querySelector('.ag-slider__track');
        const list = [...slides];
        for (let i = 0; i < list.length; i += 4) {
          const g = list.slice(i, i + 4);
          [g[0], g[2], g[1], g[3]].filter(Boolean).forEach((el) => track0.append(el));
        }
      }
      if (phone && slides.length > 4 && !root.querySelector('.ag-slider__dots')) {
        const track = root.querySelector('.ag-slider__track');
        const pages = Math.ceil(slides.length / 4);
        const dots = document.createElement('div');
        dots.className = 'ag-slider__dots';
        dots.innerHTML = Array.from({ length: pages }, (_, i) => `<button type="button" aria-label="Page ${i + 1}"${i ? '' : ' aria-current="true"'}></button>`).join('');
        track.after(dots);
        const pageW = () => track.scrollWidth / pages;
        dots.addEventListener('click', (e) => { const b = e.target.closest('button'); if (b) track.scrollTo({ left: [...dots.children].indexOf(b) * pageW(), behavior: 'smooth' }); });
        track.addEventListener('scroll', () => {
          const k = Math.round(track.scrollLeft / pageW());
          [...dots.children].forEach((d, i) => d.toggleAttribute('aria-current', i === k));
        }, { passive: true });
      }
      return;
    }
    if (reduced || !hasGsap() || !scenes[name]) return;
    root._agCtx?.revert();
    root._agCtx = gsap.context(() => scenes[name](root), root);
  }

  /* ---------- Review carousel (arrows + gentle auto-advance, all viewports) ---------- */
  function initReviewCarousels() {
    document.querySelectorAll('.ag-why__quotes').forEach((quotes) => {
      if (quotes._agRv) return;
      const track = quotes.querySelector('.ag-why__track');
      if (!track) return;
      quotes._agRv = true;
      // One set of cards only — drop the duplicate (aria-hidden) cards the marquee used,
      // so a scrollable carousel doesn't show everything twice.
      track.querySelectorAll('.ag-why__quote[aria-hidden="true"]').forEach((el) => el.remove());
      if (!track.querySelector('.ag-why__quote')) return;
      quotes.classList.add('ag-why__quotes--carousel');

      const step = () => {
        const c = track.querySelector('.ag-why__quote');
        return c ? c.offsetWidth + 18 : track.clientWidth * 0.8;
      };
      const atEnd = () => track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      const atStart = () => track.scrollLeft <= 4;
      const go = (dir) => {
        if (dir > 0 && atEnd()) { track.scrollTo({ left: 0, behavior: 'smooth' }); return; }
        if (dir < 0 && atStart()) { track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' }); return; }
        track.scrollBy({ left: dir * step(), behavior: 'smooth' });
      };
      const arrow = (dir) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ag-rv-arrow ag-why__arrow ag-why__arrow--' + (dir < 0 ? 'prev' : 'next');
        b.setAttribute('aria-label', dir < 0 ? 'Previous review' : 'Next review');
        b.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="' + (dir < 0 ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6') + '"/></svg>';
        b.addEventListener('click', () => { go(dir); pause(); });
        return b;
      };
      quotes.append(arrow(-1), arrow(1));

      // Gentle auto-advance on pointer (desktop) devices; never on touch, reduced-motion, or when hidden.
      const canAuto = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches && !reduced;
      let timer = null;
      const play = () => { if (canAuto && !timer) timer = setInterval(() => go(1), 3500); };
      const pause = () => { if (timer) { clearInterval(timer); timer = null; } };
      if (canAuto) {
        quotes.addEventListener('pointerenter', pause);
        quotes.addEventListener('pointerleave', play);
        track.addEventListener('pointerdown', pause);
        const io = new IntersectionObserver((es) => es.forEach((en) => (en.isIntersecting ? play() : pause())), { threshold: 0.2 });
        io.observe(quotes);
      }
    });
  }

  /* ---------- Product form (AJAX add to cart) ---------- */
  function initProductForms() {
    document.querySelectorAll('[data-ag-product]').forEach((wrap) => {
      if (wrap._agReady) return; wrap._agReady = true;
      const variants = JSON.parse(wrap.querySelector('[data-ag-variants]')?.textContent || '[]');
      const form = wrap.querySelector('form[action*="/cart/add"]');
      const idInput = form?.querySelector('[name="id"]');
      const priceEl = wrap.querySelector('[data-ag-price]');
      const btn = form?.querySelector('[type="submit"]');
      const msg = wrap.querySelector('.ag-pdp__msg');
      const money = (c) => (window.Shopify?.formatMoney ? Shopify.formatMoney(c) : new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: wrap.dataset.currency || 'USD' }).format(c / 100));

      wrap.querySelectorAll('.ag-pdp__option input').forEach((inp) => inp.addEventListener('change', () => {
        const chosen = [...wrap.querySelectorAll('.ag-pdp__option')].map((f) => f.querySelector('input:checked')?.value);
        const v = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
        if (!v) { btn.disabled = true; btn.textContent = btn.dataset.unavailable; return; }
        idInput.value = v.id;
        btn.disabled = !v.available;
        btn.textContent = v.available ? btn.dataset.add : btn.dataset.soldout;
        wrap.querySelectorAll('[data-ag-buynow]').forEach((b) => (b.disabled = !v.available));
        if (priceEl) {
          const onSale = v.compare_at_price > v.price;
          const off = onSale ? Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100) : 0;
          priceEl.innerHTML = `<span class="ag-price__now">${money(v.price)}${off > 0 ? `<span class="ag-saleflag">-${off}%</span>` : ''}</span>` + (onSale ? ` <s>${money(v.compare_at_price)}</s>` : '');
        }
        if (v.featured_image) wrap.querySelector('.ag-pdp__img')?.setAttribute('src', v.featured_image);
        const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url);
      }));

      wrap.querySelectorAll('[data-ag-qty]').forEach((b) => b.addEventListener('click', () => {
        const q = form.querySelector('[name="quantity"]');
        q.value = Math.max(1, Number(q.value) + Number(b.dataset.agQty));
      }));

      // Touch devices have no hover, so tap the main image to flip to the 2nd image.
      const pdpMedia = wrap.querySelector('.ag-pdp__media--hover2');
      if (pdpMedia && window.matchMedia('(hover: none)').matches) {
        pdpMedia.addEventListener('click', (e) => {
          if (e.target.closest('.ag-pdp__thumbs, button, a')) return;
          pdpMedia.classList.toggle('is-flipped');
        });
      }

      wrap.querySelectorAll('.ag-pdp__thumbs button').forEach((t) => t.addEventListener('click', () => {
        const main = wrap.querySelector('.ag-pdp__img');
        wrap.querySelector('.ag-pdp__media--hover2')?.classList.remove('is-flipped');
        wrap.querySelectorAll('.ag-pdp__thumbs button').forEach((b) => b.setAttribute('aria-current', 'false'));
        t.setAttribute('aria-current', 'true');
        if (window.gsap) gsap.fromTo(main, { opacity: 0, rotate: -10, yPercent: 8 }, { opacity: 1, rotate: 0, yPercent: 0, duration: 0.7, ease: 'expo.out' });
        main.src = t.dataset.src; main.srcset = '';
      }));

      wrap.querySelectorAll('[data-ag-buynow]').forEach((b) => b.addEventListener('click', async () => {
        b.disabled = true;
        try {
          const res = await fetch(window.Shopify?.routes?.root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!res.ok) throw new Error((await res.json()).description);
          location.href = (window.Shopify?.routes?.root || '/') + 'checkout';
        } catch (err) { msg.textContent = err.message; b.disabled = false; }
      }));

      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        btn.disabled = true;
        try {
          const res = await fetch(window.Shopify?.routes?.root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message);
          msg.textContent = wrap.dataset.added;
          updateCartCount();
          Cart.open();
        } catch (err) { msg.textContent = err.message; }
        btn.disabled = false;
      });

      // Desktop: a floating rounded checkout dock that slides up once the main buttons scroll off-screen.
      if (window.matchMedia('(min-width: 750px)').matches && 'IntersectionObserver' in window) {
        const buyRow = wrap.querySelector('.ag-pdp__buy');
        const buyNow = wrap.querySelector('[data-ag-buynow]');
        if (buyRow && btn && !document.querySelector('.ag-pdp__dock')) {
          const dock = document.createElement('div');
          dock.className = 'ag-pdp__dock';
          dock.innerHTML =
            '<span class="ag-pdp__dock-info"><b class="ag-pdp__dock-name"></b><span class="ag-pdp__dock-price"></span></span>' +
            '<button type="button" class="ag-btn ag-btn--bag ag-pdp__dock-add"></button>' +
            (buyNow ? '<button type="button" class="ag-btn ag-btn--order ag-pdp__dock-buy"></button>' : '');
          document.body.appendChild(dock);
          dock.querySelector('.ag-pdp__dock-name').textContent = (document.querySelector('.ag-pdp__title')?.textContent || '').trim();
          const dockAdd = dock.querySelector('.ag-pdp__dock-add');
          const dockBuy = dock.querySelector('.ag-pdp__dock-buy');
          const sync = () => {
            dockAdd.textContent = btn.textContent;
            dockAdd.toggleAttribute('disabled', btn.disabled);
            if (dockBuy) dockBuy.textContent = buyNow.textContent || 'Order now';
            const dp = dock.querySelector('.ag-pdp__dock-price');
            if (dp && priceEl) {
              const now = priceEl.querySelector('.ag-price__now') || priceEl;
              let txt = now.textContent.trim();
              const flag = now.querySelector?.('.ag-saleflag')?.textContent;
              if (flag) txt = txt.replace(flag, '').trim();
              dp.textContent = txt;
            }
          };
          sync();
          dockAdd.addEventListener('click', () => btn.click());
          dockBuy?.addEventListener('click', () => buyNow.click());
          if (priceEl) new MutationObserver(sync).observe(priceEl, { childList: true, subtree: true });
          new IntersectionObserver(([e]) => {
            dock.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0);
          }, { threshold: 0 }).observe(buyRow);
        }
      }
    });
  }

  async function updateCartCount() {
    try {
      const cart = await (await fetch(window.Shopify?.routes?.root + 'cart.js')).json();
      document.querySelectorAll('[data-ag-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        if (window.gsap) gsap.fromTo(el, { scale: 1.6 }, { scale: 1, duration: 0.6, ease: 'elastic.out(1, .4)' });
      });
    } catch (e) {}
  }

  /* ---------- Warm the product page before the click (kills the late-image jerk) ---------- */
  function initPreload() {
    const done = new Set();
    const warm = (e) => {
      const link = e.target.closest && e.target.closest('[data-ag-pdp-src]');
      if (!link) return;
      const src = link.dataset.agPdpSrc;
      if (src && !done.has(src)) {
        done.add(src);
        const img = new Image(); img.decoding = 'async'; img.fetchPriority = 'high'; img.src = src;
        img.decode?.().catch(() => {});
      }
      const href = link.getAttribute('href');
      if (href && !done.has(href) && !HTMLScriptElement.supports?.('speculationrules')) {
        done.add(href);
        const l = document.createElement('link'); l.rel = 'prefetch'; l.href = href; document.head.appendChild(l);
      }
    };
    document.addEventListener('pointerover', warm, { passive: true });
    document.addEventListener('touchstart', warm, { passive: true });
    document.addEventListener('focusin', warm);
  }

  /* ---------- Cart drawer ---------- */
  const Cart = (() => {
    const root = document.getElementById('AgCart');
    if (!root) return { open() {}, refresh() {} };
    const items = root.querySelector('[data-ag-cart-items]');
    const empty = root.querySelector('[data-ag-cart-empty]');
    const foot = root.querySelector('[data-ag-cart-foot]');
    const sub = root.querySelector('[data-ag-cart-subtotal]');
    let currency = 'PKR';
    try { currency = JSON.parse(document.querySelector('[data-ag-cart-currency]').textContent); } catch (e) {}
    const money = (c) => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(c / 100);
    const base = (window.Shopify?.routes?.root || '/');
    const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const render = (cart) => {
      document.querySelectorAll('[data-ag-cart-count]').forEach((el) => (el.textContent = cart.item_count));
      empty.hidden = cart.item_count > 0; foot.hidden = cart.item_count === 0;
      sub.textContent = money(cart.total_price);
      items.innerHTML = cart.items.map((it, i) => `
        <div class="ag-cart__item" data-line="${i + 1}">
          <a class="ag-cart__thumb" href="${esc(it.url)}">${it.image ? `<img src="${esc(it.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2'))}" alt="" width="80" height="80" loading="lazy">` : ''}</a>
          <div class="ag-cart__info">
            <a class="ag-cart__name" href="${esc(it.url)}">${esc(it.product_title)}</a>
            ${it.variant_title && it.variant_title !== 'Default Title' ? `<span class="ag-cart__variant">${esc(it.variant_title)}</span>` : ''}
            <div class="ag-cart__line">
              <div class="ag-qty ag-qty--sm"><button type="button" data-q="-1" aria-label="Decrease">−</button><span>${it.quantity}</span><button type="button" data-q="1" aria-label="Increase">+</button></div>
              <strong>${money(it.final_line_price)}</strong>
            </div>
            <button class="ag-cart__remove" type="button" data-remove>Remove</button>
          </div>
        </div>`).join('');
      if (window.gsap) gsap.from(items.children, { y: 20, opacity: 0, stagger: 0.05, duration: 0.5, ease: 'power3.out', clearProps: 'transform,opacity' });
    };
    const refresh = async () => render(await (await fetch(base + 'cart.js')).json());
    const change = async (line, quantity) => {
      root.classList.add('is-busy');
      const res = await fetch(base + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
      render(await res.json()); root.classList.remove('is-busy');
    };
    items.addEventListener('click', (e) => {
      const row = e.target.closest('.ag-cart__item'); if (!row) return;
      const line = Number(row.dataset.line);
      const qty = Number(row.querySelector('.ag-qty span').textContent);
      if (e.target.closest('[data-remove]')) change(line, 0);
      const q = e.target.closest('[data-q]'); if (q) change(line, Math.max(0, qty + Number(q.dataset.q)));
    });
    const open = async () => {
      await refresh();
      root.classList.add('is-open'); root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ag-no-scroll');
      root.querySelector('.ag-cart__panel').focus?.();
    };
    const close = () => { root.classList.remove('is-open'); root.setAttribute('aria-hidden', 'true'); document.documentElement.classList.remove('ag-no-scroll'); };
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-ag-cart-open]')) { e.preventDefault(); open(); }
      if (e.target.closest('[data-ag-cart-close]')) close();
    });
    document.addEventListener('keydown', (e) => e.key === 'Escape' && close());
    return { open, refresh };
  })();

  /* ---------- "Add to Bag" on product cards ([data-ag-add] = variant id): add 1, open the cart drawer ----------
     The button sits inside the card link, so the click must not follow the link. */
  const agAdd = async (b) => {
    if (b.classList.contains('is-busy')) return;
    b.classList.add('is-busy');
    const base = window.Shopify?.routes?.root || '/';
    try {
      const r = await fetch(base + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(b.dataset.agAdd), quantity: 1 }] }) });
      if (r.ok) { if (Cart?.open) await Cart.open(); else location.href = base + 'cart'; }
    } catch (err) {}
    b.classList.remove('is-busy');
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-ag-add]'); if (!b) return;
    e.preventDefault(); e.stopPropagation(); agAdd(b);
  }, true);
  document.addEventListener('keydown', (e) => {
    const b = e.target.closest?.('[data-ag-add]'); if (b && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); agAdd(b); }
  });

  /* ---------- Search overlay (predictive) ---------- */
  (() => {
    const root = document.getElementById('AgSearch');
    if (!root) return;
    const input = root.querySelector('[data-ag-search-input]');
    const out = root.querySelector('[data-ag-search-results]');
    const label = root.querySelector('[data-ag-search-label]');
    const chips = root.querySelector('.ag-search__chips');
    const base = window.Shopify?.routes?.root || '/';
    const esc = (t) => String(t ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    let timer, ctrl;
    let closeTimer;
    const open = () => {
      clearTimeout(closeTimer); root.classList.remove('is-closing');
      root.classList.add('is-open'); root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ag-no-scroll');
      setTimeout(() => input.focus(), 250);
    };
    const close = () => {
      if (!root.classList.contains('is-open')) return;
      // Play the reverse animation: keep the overlay painted via .is-closing while the
      // panel/body/backdrop transition back out, then hide it once that finishes.
      root.classList.add('is-closing');
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ag-no-scroll');
      if (document.activeElement && root.contains(document.activeElement)) document.activeElement.blur();
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => root.classList.remove('is-closing'), 520);
    };
    const show = (products, q) => {
      label.textContent = q ? (products.length ? `Results for “${q}”` : `No results for “${q}”`) : label.dataset.default;
      chips.hidden = !!q;
      out.innerHTML = products.map((p) => `
        <a class="ag-search__item" href="${esc(p.url)}">
          <span class="ag-search__thumb">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy" width="120" height="120">` : ''}</span>
          <span class="ag-search__name">${esc(p.title)}</span>
          <span class="ag-search__price">${esc(p.price_text || '')}</span>
        </a>`).join('') + (q && products.length ? `<a class="ag-btn ag-search__all" href="${base}search?q=${encodeURIComponent(q)}&type=product">View all results</a>` : '');
      if (window.gsap) gsap.from(out.querySelectorAll('.ag-search__item'), { y: 24, opacity: 0, stagger: 0.05, duration: 0.5, ease: 'power3.out', clearProps: 'transform,opacity' });
    };
    label.dataset.default = label.textContent;
    const search = async (q) => {
      if (!q) { show([], ''); return; }
      ctrl?.abort(); ctrl = new AbortController();
      try {
        const res = await fetch(`${base}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`, { signal: ctrl.signal });
        const data = await res.json();
        const items = (data.resources?.results?.products || []).map((p) => ({ url: p.url, title: p.title, image: p.image ? p.image.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2') : '', price_text: p.price ? new Intl.NumberFormat(undefined, { style: 'currency', currency: document.querySelector('[data-ag-cart-currency]') ? JSON.parse(document.querySelector('[data-ag-cart-currency]').textContent) : 'PKR' }).format(Number(p.price)) : '' }));
        show(items, q);
      } catch (e) { if (e.name !== 'AbortError') show([], q); }
    };
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => search(input.value.trim()), 180); });
    root.querySelectorAll('[data-ag-search-term]').forEach((b) => b.addEventListener('click', () => { input.value = b.dataset.agSearchTerm; search(input.value); input.focus(); }));
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-ag-search-open]')) { e.preventDefault(); open(); }
      if (e.target.closest('[data-ag-search-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); open(); }
    });
  })();

  /* ---------- "Order now" anywhere ([data-ag-buy-now] = variant id): add 1 → checkout ----------
     Page-wide, so it works even when the section's animations don't run (no GSAP / reduced motion). */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-ag-buy-now]'); if (!b || b.disabled) return;
    e.preventDefault();
    b.disabled = true; b.classList.add('is-busy');
    const base = window.Shopify?.routes?.root || '/';
    try {
      const r = await fetch(base + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(b.dataset.agBuyNow), quantity: 1 }] }) });
      if (r.ok) { location.href = base + 'checkout'; return; }
    } catch (err) {}
    b.disabled = false; b.classList.remove('is-busy');
  });

  /* ---------- Boot ---------- */
  function boot() {
    if (hasGsap()) gsap.registerPlugin(ScrollTrigger);
    initLenis();
    initHeader();
    initPreload();
    initProductForms();
    initReviewCarousels();
    document.querySelectorAll('.ag-hero__title').forEach(splitWords);
    initLoader().then(() => {
      document.documentElement.classList.add('ag-loaded');
      document.querySelectorAll('[data-ag-scene]').forEach(initSection);
      if (hasGsap()) ScrollTrigger.refresh();
    });
    window.addEventListener('load', () => hasGsap() && ScrollTrigger.refresh());
  }

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-ag-scene]').forEach(initSection);
    initProductForms();
    initReviewCarousels();
    if (hasGsap()) ScrollTrigger.refresh();
  });

  // A page prerendered on hover must not play its entrance (bottle rise etc.) while still hidden —
  // wait until the visitor actually lands on it.
  const start = () => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot());
  if (document.prerendering) document.addEventListener('prerenderingchange', start, { once: true });
  else start();
})();

/* PDP (mobile/tablet): white header logo while the coloured product image sits behind the header,
   dark logo once the lighter detail sections scroll up. Skips when the logo is permanently white. */
(() => {
  const header = document.querySelector('.ag-header');
  const media = document.querySelector('.ag-pdp__media');
  if (!header || !media || header.classList.contains('ag-header--logo-white')) return;
  const mq = window.matchMedia('(max-width: 989px)');
  let state;
  const update = () => {
    const over = mq.matches && media.getBoundingClientRect().bottom > header.getBoundingClientRect().height * 0.55;
    if (over === state) return;
    state = over;
    header.classList.toggle('ag-header--on-media', over);
  };
  update();
  addEventListener('scroll', update, { passive: true });
  addEventListener('resize', update);
  mq.addEventListener?.('change', update);
})();

/* Mobile: switch the header logo to white once the dark footer scrolls up behind the header. */
(() => {
  const header = document.querySelector('.ag-header');
  const footer = document.querySelector('.ag-footer');
  if (!header || !footer || header.classList.contains('ag-header--logo-white')) return;
  const mq = window.matchMedia('(max-width: 989px)');
  let state;
  const update = () => {
    const hb = header.getBoundingClientRect().bottom;
    const over = mq.matches && footer.getBoundingClientRect().top < hb;
    if (over === state) return;
    state = over;
    header.classList.toggle('ag-header--on-dark', over);
  };
  update();
  addEventListener('scroll', update, { passive: true });
  addEventListener('resize', update);
  mq.addEventListener?.('change', update);
})();
